/**
 * @file order-item-link.ts
 * @description Позиция заказа без товара каталога: признак, тексты и ошибки
 * привязки (phase19.24 шаг 5, OQ-54).
 *
 * Такую позицию нельзя собрать: сервер не даёт провести задание (409
 * `picking_item_unlinked`), и заказ стоит, пока менеджер не привяжет товар.
 * Признак один на всю карточку — «`skuId === null` и это не услуга», поэтому
 * он живёт здесь, а не повторяется в каждой вкладке: разъехавшиеся условия
 * дали бы красную строку во вкладке и спокойную полосу «Сейчас».
 *
 * Тексты и расшифровка кодов ошибок — реестрами: новый код сервера = запись,
 * а не ветка в диалоге.
 */

import { ApiError } from "./api-client";
import { pluralRu } from "./plural";

/**
 * Минимум, который нужен признаку. Структурный тип нарочно: одну и ту же
 * проверку делают и краткие строки карточки (`OrderItemBriefDto`), и сырые
 * позиции редактора (`OrderItemRawDto`).
 */
export interface OrderItemLinkView {
  id: string;
  skuId: string | null;
  /** Позиция-услуга «Доставка»: SKU у неё нет по определению. */
  isService?: boolean;
}

/** Позиция не привязана к товару каталога — собрать её невозможно. */
export function isUnlinkedOrderItem(item: OrderItemLinkView): boolean {
  return item.skuId === null && item.isService !== true;
}

/** Непривязанные позиции по порядку состава; пусто — состав в порядке. */
export function unlinkedOrderItems<T extends OrderItemLinkView>(
  items: readonly T[] | null | undefined,
): T[] {
  return (items ?? []).filter(isUnlinkedOrderItem);
}

/**
 * Тексты проблемы — одними словами во вкладке, полосе «Сейчас» и задании
 * сборки. Менеджер не должен гадать, одно это событие или три разных.
 */
export const ORDER_ITEM_UNLINKED_TEXT = {
  /** Стикер строки и карточки состава. */
  chip: "Нужно решение",
  /** Объяснение под названием позиции. */
  row: "Позиция не привязана к товару каталога — сборка невозможна",
  /** Подпись кнопки-действия: одна на строку, «⋯» и полосу «Сейчас». */
  action: "Привязать товар",
  /** Заголовок подтверждения перед вызовом сервера. */
  confirmTitle: "Привязать товар к позиции",
  /** Ссылка из задания сборки в карточку заказа. */
  openOrderItems: "Открыть заказ → вкладка Позиции",
} as const;

/**
 * «1 позиция не привязана» / «3 позиции не привязаны» — подлежащее со
 * сказуемым для первой строки полосы «Сейчас». Склоняем и существительное, и
 * глагол: «1 позиция не привязаны» читается как сбой интерфейса.
 */
export function unlinkedCountPhrase(count: number): string {
  const noun = pluralRu(count, ["позиция", "позиции", "позиций"]);
  const verb = pluralRu(count, ["привязана", "привязаны", "привязаны"]);
  return `${count} ${noun} не ${verb}`;
}

/**
 * Текст подтверждения перед привязкой. Цена и пересборка задания названы
 * прямо: менеджер должен знать, что отметки кладовщика сбросятся, ДО нажатия,
 * а не из тоста после.
 */
export function relinkConfirmText(input: {
  itemName: string;
  skuName: string;
  priceGross: string;
}): string {
  return (
    `Позиция «${input.itemName}» станет «${input.skuName}». ` +
    `Цена позиции сохранится: ${input.priceGross} ₽. ` +
    "Задание сборки будет пересобрано, отметки кладовщика сбросятся."
  );
}

/**
 * Ошибки привязки человеческим языком. Сервер присылает `message`, но по этим
 * кодам менеджеру нужна ещё и подсказка «что делать», поэтому текст наш.
 */
const NOT_FOUND = "Позиция или заказ не найдены — обновите страницу";

const RELINK_ERROR_TEXT: Record<string, string> = {
  order_item_already_linked:
    "Позицию уже привязали — обновите страницу, чтобы увидеть актуальный состав",
  order_item_relink_stage:
    "На этой стадии позицию привязать нельзя — состав заказа уже закрыт",
  not_found: NOT_FOUND,
};

export function relinkErrorText(
  err: unknown,
  fallback = "Не удалось привязать товар к позиции",
): string {
  if (err instanceof ApiError) {
    const known = RELINK_ERROR_TEXT[err.code] ?? (err.status === 404 ? NOT_FOUND : null);
    return known ?? (err.message || fallback);
  }
  return err instanceof Error && err.message ? err.message : fallback;
}
